import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import FormInput from "../components/FormInput";
import PasswordFormField from "../components/PasswordFormField";
import FormSelect from "../components/FormSelect";
import PrimaryButton from "../components/PrimaryButton";
import { getCreateTanneryUserErrorMessage } from "../utils/createTanneryUserApiErrors";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";
const ROLE_OPTIONS = ["DataEntry", "TokenGenerator", "TanneryAdmin", "Executive"];
const TANNERY_REQUIRED_ROLES = new Set(["TOKENGENERATOR", "DATAENTRY", "TANNERYADMIN"]);
const INITIAL_FORM = {
  role: "",
  tannery_id: "",
  name: "",
  username: "",
  password: "",
};

const INITIAL_EDIT_FORM = {
  name: "",
  username: "",
  password: "",
  role: "",
  tannery_id: "",
};

const normalizeRole = (role) =>
  String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

const normalizeCompactRole = (role) =>
  String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s_-]/g, "");

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

const parseJwtPayload = (token) => {
  if (!token) {
    return null;
  }
  try {
    const tokenParts = token.split(".");
    if (tokenParts.length < 2) {
      return null;
    }
    const base64Url = tokenParts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(paddedBase64));
  } catch {
    return null;
  }
};

const formatRoleLabel = (role) => {
  const r = String(role || "").trim();
  if (!r) {
    return "—";
  }
  return r.replace(/([a-z])([A-Z])/g, "$1 $2");
};

function buildPatchPayload(snapshot, form, { isSuperAdminUser }) {
  const body = {};
  const nameTrim = form.name.trim();
  const userTrim = form.username.trim();

  if (isSuperAdminUser) {
    if (nameTrim !== snapshot.name) {
      body.name = nameTrim;
    }
    if (userTrim !== snapshot.username) {
      body.username = userTrim;
    }
    return { body };
  }

  if (nameTrim !== snapshot.name) {
    body.name = nameTrim;
  }
  if (userTrim !== snapshot.username) {
    body.username = userTrim;
  }
  if (form.password.trim()) {
    body.password = form.password.trim();
  }
  if (form.role !== snapshot.role) {
    body.role = form.role;
  }

  const roleCompact = normalizeCompactRole(form.role);
  const needsTannery = TANNERY_REQUIRED_ROLES.has(roleCompact);
  const newTanneryId = needsTannery && form.tannery_id.trim() ? Number(form.tannery_id) : null;
  const oldTanneryId = snapshot.tannery_id != null ? Number(snapshot.tannery_id) : null;

  if (form.role !== snapshot.role) {
    body.tannery_id = needsTannery ? newTanneryId : null;
  } else if (needsTannery && newTanneryId !== oldTanneryId) {
    body.tannery_id = newTanneryId;
  } else if (!needsTannery && oldTanneryId != null) {
    body.tannery_id = null;
  }

  return { body };
}

function SuperAdminUsersPage() {
  const navigate = useNavigate();
  const isSuperAdmin = normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY)) === "SUPER_ADMIN";

  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState("");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createModalError, setCreateModalError] = useState("");

  const [isLoadingTanneries, setIsLoadingTanneries] = useState(false);
  const [tanneries, setTanneries] = useState([]);

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(INITIAL_EDIT_FORM);
  const [editSnapshot, setEditSnapshot] = useState(null);
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [editModalError, setEditModalError] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [togglingUserId, setTogglingUserId] = useState("");

  const tanneryOptions = useMemo(
    () => tanneries.map((item) => ({ value: item.id, label: item.name })),
    [tanneries]
  );

  const currentUserId = useMemo(() => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    const payload = parseJwtPayload(token);
    if (!payload || typeof payload !== "object") {
      return "";
    }
    return String(payload.user_id ?? payload.sub ?? payload.id ?? "").trim();
  }, []);

  const currentUsernameFromToken = useMemo(() => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    const payload = parseJwtPayload(token);
    if (!payload || typeof payload !== "object") {
      return "";
    }
    return String(payload.username ?? payload.preferred_username ?? "").trim();
  }, []);

  const parseApiMessage = async (response, fallback) => {
    const text = await response.text();
    if (!text) {
      return fallback;
    }
    try {
      const payload = JSON.parse(text);
      return payload?.message || payload?.detail || fallback;
    } catch (error) {
      return text;
    }
  };

  const fetchTanneries = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsLoadingTanneries(true);
      const response = await fetch(`${getBaseApiUrl()}/super-admin/tanneries`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(await parseApiMessage(response, "Unable to load tanneries."));
      }

      const payload = await response.json();
      const list = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
      const normalized = list
        .map((item) => ({
          id: String(item?.id ?? item?.tannery_id ?? "").trim(),
          name: String(item?.tannery_name ?? item?.name ?? "").trim(),
        }))
        .filter((item) => item.id && item.name);
      setTanneries(normalized);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load tanneries.");
    } finally {
      setIsLoadingTanneries(false);
    }
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsLoadingUsers(true);
      setUsersError("");
      const response = await fetch(`${getBaseApiUrl()}/super-admin/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(await parseApiMessage(response, "Unable to load users."));
      }

      const payload = await response.json();
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];
      setUsers(
        list.map((row) => ({
          user_id: String(row?.user_id ?? "").trim(),
          name: row?.name ?? "",
          username: row?.username ?? "",
          role: row?.role ?? "",
          tannery_id: row?.tannery_id ?? null,
          tannery_name: row?.tannery_name ?? null,
          is_active: Boolean(row?.is_active),
        }))
      );
    } catch (error) {
      setUsersError(error.message || "Unable to load users.");
    } finally {
      setIsLoadingUsers(false);
    }
  }, [navigate]);

  const selectedRoleCompact = normalizeCompactRole(formData.role);
  const requiresTannery = TANNERY_REQUIRED_ROLES.has(selectedRoleCompact);
  const nameRequired =
    selectedRoleCompact === "TOKENGENERATOR" || selectedRoleCompact === "DATAENTRY";

  const editRoleCompact = normalizeCompactRole(editForm.role);
  const editRequiresTannery = TANNERY_REQUIRED_ROLES.has(editRoleCompact);
  const editNameRequired =
    editRoleCompact === "TOKENGENERATOR" || editRoleCompact === "DATAENTRY";
  const isEditingSuperAdmin =
    editSnapshot && normalizeCompactRole(editSnapshot.role) === "SUPERADMIN";

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    fetchUsers();
    fetchTanneries();
  }, [fetchUsers, fetchTanneries, isSuperAdmin]);

  const isRowSelf = (row) => {
    if (currentUserId && row.user_id && row.user_id === currentUserId) {
      return true;
    }
    if (currentUsernameFromToken && row.username && row.username === currentUsernameFromToken) {
      return true;
    }
    return false;
  };

  const handleToggleRestriction = async (user) => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setUsersError("");
      setMessage("");
      setTogglingUserId(String(user.user_id));

      const response = await fetch(
        `${getBaseApiUrl()}/super-admin/users/${encodeURIComponent(user.user_id)}/restriction`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            is_active: !user.is_active,
          }),
        }
      );

      const responseText = await response.text();
      let responsePayload = null;
      if (responseText) {
        try {
          responsePayload = JSON.parse(responseText);
        } catch {
          responsePayload = null;
        }
      }

      if (!response.ok) {
        throw new Error(
          (responsePayload && (responsePayload.message || responsePayload.detail)) ||
            responseText ||
            "Unable to update user restriction."
        );
      }

      const data =
        responsePayload?.data && typeof responsePayload.data === "object"
          ? responsePayload.data
          : responsePayload;
      const nextActive =
        data && typeof data === "object" && "is_active" in data
          ? Boolean(data.is_active)
          : !user.is_active;

      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === user.user_id
            ? {
                ...u,
                is_active: nextActive,
                ...(data && typeof data === "object" && data.username ? { username: data.username } : {}),
                ...(data && typeof data === "object" && data.role ? { role: data.role } : {}),
              }
            : u
        )
      );
      setMessage(`Restriction updated for "${data?.username || user.username}".`);
    } catch (error) {
      setUsersError(error.message || "Unable to update user restriction.");
    } finally {
      setTogglingUserId("");
    }
  };

  const openCreateModal = () => {
    setFormData(INITIAL_FORM);
    setFieldErrors({});
    setCreateModalError("");
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateModalError("");
    setFieldErrors({});
  };

  const openEditModal = (user) => {
    const tid = user.tannery_id != null && user.tannery_id !== "" ? String(user.tannery_id) : "";
    setEditingUser(user);
    setEditForm({
      name: user.name || "",
      username: user.username || "",
      password: "",
      role: user.role || "",
      tannery_id: tid,
    });
    setEditSnapshot({
      name: (user.name || "").trim(),
      username: (user.username || "").trim(),
      role: user.role || "",
      tannery_id: user.tannery_id != null ? Number(user.tannery_id) : null,
    });
    setEditFieldErrors({});
    setEditModalError("");
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditForm(INITIAL_EDIT_FORM);
    setEditSnapshot(null);
    setEditFieldErrors({});
    setEditModalError("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "role" && normalizeCompactRole(value) === "EXECUTIVE") {
        next.tannery_id = "";
        next.name = "";
      }
      return next;
    });
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setCreateModalError("");
    setMessage("");
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "role" && normalizeCompactRole(value) === "EXECUTIVE") {
        next.tannery_id = "";
        next.name = "";
      }
      return next;
    });
    setEditFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setEditModalError("");
  };

  const validate = () => {
    const errors = {};
    if (!formData.role.trim()) {
      errors.role = "Please select a role.";
    }
    if (requiresTannery && !formData.tannery_id.trim()) {
      errors.tannery_id = "Please select a tannery.";
    }
    if (nameRequired && !formData.name.trim()) {
      errors.name = "Name is required.";
    }
    if (!formData.username.trim()) {
      errors.username = "Username is required.";
    }
    if (!formData.password.trim()) {
      errors.password = "Password is required.";
    }
    return errors;
  };

  const validateEdit = () => {
    const errors = {};
    if (!editForm.username.trim()) {
      errors.username = "Username is required.";
    }
    if (!isEditingSuperAdmin) {
      if (!editForm.role.trim()) {
        errors.role = "Please select a role.";
      }
      if (editRequiresTannery && !editForm.tannery_id.trim()) {
        errors.tannery_id = "Please select a tannery.";
      }
      if (editNameRequired && !editForm.name.trim()) {
        errors.name = "Name is required.";
      }
    } else if (!editForm.name.trim()) {
      errors.name = "Name is required.";
    }
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    const payload = {
      role: formData.role,
      username: formData.username.trim(),
      password: formData.password,
    };
    if (requiresTannery) {
      payload.tannery_id = Number(formData.tannery_id);
      const trimmedName = formData.name.trim();
      if (trimmedName) {
        payload.name = trimmedName;
      }
    }

    try {
      setIsSubmitting(true);
      setCreateModalError("");

      const response = await fetch(`${getBaseApiUrl()}/super-admin/tannery-users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let responsePayload = null;
      if (responseText) {
        try {
          responsePayload = JSON.parse(responseText);
        } catch (error) {
          responsePayload = null;
        }
      }

      if (!response.ok) {
        const rawApiMessage =
          (responsePayload && (responsePayload.message || responsePayload.detail)) ||
          responseText ||
          "";
        throw new Error(
          getCreateTanneryUserErrorMessage(response.status, responsePayload, rawApiMessage)
        );
      }

      const createdUsername = responsePayload?.username || payload.username;
      const createdRole = responsePayload?.role || payload.role;
      setMessage(`User "${createdUsername}" created as ${createdRole}.`);
      setFormData(INITIAL_FORM);
      setFieldErrors({});
      closeCreateModal();
      fetchUsers();
    } catch (error) {
      setCreateModalError(error.message || "Unable to create user right now.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();
    if (!editingUser || !editSnapshot) {
      return;
    }

    const errors = validateEdit();
    if (Object.keys(errors).length > 0) {
      setEditFieldErrors(errors);
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    const superRow = normalizeCompactRole(editSnapshot.role) === "SUPERADMIN";
    const { body, error: buildError } = buildPatchPayload(editSnapshot, editForm, {
      isSuperAdminUser: superRow,
    });

    if (buildError) {
      setEditModalError(buildError);
      return;
    }

    if (!body || Object.keys(body).length === 0) {
      setEditModalError("No changes to save.");
      return;
    }

    try {
      setIsSavingEdit(true);
      setEditModalError("");

      const response = await fetch(
        `${getBaseApiUrl()}/super-admin/users/${encodeURIComponent(editingUser.user_id)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      const responseText = await response.text();
      let responsePayload = null;
      if (responseText) {
        try {
          responsePayload = JSON.parse(responseText);
        } catch (error) {
          responsePayload = null;
        }
      }

      if (!response.ok) {
        const apiMessage =
          (responsePayload && (responsePayload.message || responsePayload.detail)) ||
          responseText ||
          "Unable to update user.";
        throw new Error(apiMessage);
      }

      setMessage(`User "${editForm.username.trim()}" updated.`);
      closeEditModal();
      fetchUsers();
    } catch (error) {
      setEditModalError(error.message || "Unable to update user.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <section className="dashboard-card" id="super-admin-create-users">
      <div className="dashboard-users-list-header">
        <h2>Users</h2>
        <div className="dashboard-header-actions">
          <PrimaryButton type="button" onClick={openCreateModal}>
            New user
          </PrimaryButton>
          <PrimaryButton type="button" onClick={fetchUsers} disabled={isLoadingUsers}>
            {isLoadingUsers ? "Refreshing..." : "Refresh"}
          </PrimaryButton>
        </div>
      </div>
      <p className="dashboard-card-subtitle">
        All users with tannery context. Create users from the popup. Use Restrict / Unrestrict for
        access (any role). Edit changes name, username, password, role, and tannery assignment;
        Super Admin accounts can only change name and username here.
      </p>

      {message ? (
        <p className="dashboard-success-message" role="status">
          {message}
        </p>
      ) : null}
      {errorMessage ? (
        <p className="dashboard-error-message" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {usersError ? (
        <p className="dashboard-error-message" role="alert">
          {usersError}
        </p>
      ) : null}

      {isLoadingUsers ? <p>Loading users...</p> : null}
      {!isLoadingUsers && users.length === 0 ? <p>No users found.</p> : null}

      {!isLoadingUsers && users.length > 0 ? (
        <div className="dashboard-users-table-wrapper">
          <table className="dashboard-users-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Username</th>
                <th scope="col">Role</th>
                <th scope="col">Tannery</th>
                <th scope="col">Status</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((row) => (
                <tr key={row.user_id}>
                  <td>{row.name || "—"}</td>
                  <td>{row.username}</td>
                  <td>{formatRoleLabel(row.role)}</td>
                  <td>{row.tannery_name || "—"}</td>
                  <td>
                    <span
                      className={`user-status-badge ${
                        row.is_active ? "user-status-badge-active" : "user-status-badge-restricted"
                      }`}
                    >
                      {row.is_active ? "Active" : "Restricted"}
                    </span>
                  </td>
                  <td>
                    <div className="token-actions-cell">
                      <button
                        type="button"
                        className={`restriction-toggle-btn ${
                          row.is_active
                            ? "restriction-toggle-btn-restrict"
                            : "restriction-toggle-btn-unrestrict"
                        }`}
                        onClick={() => handleToggleRestriction(row)}
                        disabled={
                          togglingUserId === String(row.user_id) ||
                          (isRowSelf(row) && row.is_active)
                        }
                      >
                        {togglingUserId === String(row.user_id)
                          ? "Updating..."
                          : row.is_active
                          ? "Restrict"
                          : "Unrestrict"}
                      </button>
                      <PrimaryButton type="button" onClick={() => openEditModal(row)}>
                        Edit
                      </PrimaryButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {isCreateModalOpen ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-user-modal-title"
          onClick={closeCreateModal}
        >
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2 id="create-user-modal-title">Create user</h2>
              <button type="button" className="modal-close-btn" onClick={closeCreateModal}>
                ×
              </button>
            </div>

            {createModalError ? (
              <p className="dashboard-error-message create-tannery-modal-error" role="alert">
                {createModalError}
              </p>
            ) : null}

            <form onSubmit={handleSubmit} noValidate>
              <div className="modal-form">
                <FormSelect
                  id="create-user-role"
                  name="role"
                  label="Role"
                  value={formData.role}
                  onChange={handleChange}
                  options={ROLE_OPTIONS}
                  placeholder="Select role"
                  error={fieldErrors.role}
                  required
                />

                {requiresTannery ? (
                  <FormSelect
                    id="create-user-tannery_id"
                    name="tannery_id"
                    label="Tannery"
                    value={formData.tannery_id}
                    onChange={handleChange}
                    options={tanneryOptions}
                    placeholder={isLoadingTanneries ? "Loading tanneries..." : "Select tannery"}
                    error={fieldErrors.tannery_id}
                    required
                    disabled={isLoadingTanneries}
                  />
                ) : null}

                <FormInput
                  id="create-user-name"
                  name="name"
                  label="Name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={
                    !requiresTannery
                      ? "Not required for Executive"
                      : nameRequired
                      ? "Enter full name"
                      : "Optional (e.g. for Tannery Admin)"
                  }
                  error={fieldErrors.name}
                  required={nameRequired}
                  disabled={!requiresTannery}
                />

                <FormInput
                  id="create-user-username"
                  name="username"
                  label="Username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter username"
                  error={fieldErrors.username}
                  required
                />

                <PasswordFormField
                  id="create-user-password"
                  name="password"
                  label="Password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  error={fieldErrors.password}
                  required
                />
              </div>
              <div className="modal-footer">
                <PrimaryButton type="button" onClick={closeCreateModal} disabled={isSubmitting}>
                  Cancel
                </PrimaryButton>
                <PrimaryButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Creating..." : "Create user"}
                </PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {editingUser ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-user-modal-title"
          onClick={closeEditModal}
        >
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2 id="edit-user-modal-title">Edit user</h2>
              <button type="button" className="modal-close-btn" onClick={closeEditModal}>
                ×
              </button>
            </div>

            {editModalError ? (
              <p className="dashboard-error-message create-tannery-modal-error" role="alert">
                {editModalError}
              </p>
            ) : null}

            <form onSubmit={handleSaveEdit} noValidate>
              <div className="modal-form">
                {isEditingSuperAdmin ? (
                  <p className="dashboard-card-subtitle super-admin-edit-hint">
                    Super Admin: role cannot be changed here. You can update name and username.
                  </p>
                ) : null}

                <FormInput
                  id="edit-user-name"
                  name="name"
                  label="Name"
                  value={editForm.name}
                  onChange={handleEditChange}
                  placeholder={
                    !editRequiresTannery && !isEditingSuperAdmin
                      ? "Not required for Executive"
                      : editNameRequired
                      ? "Enter full name"
                      : "Optional (e.g. for Tannery Admin)"
                  }
                  error={editFieldErrors.name}
                  required={isEditingSuperAdmin || editNameRequired}
                />

                <FormInput
                  id="edit-user-username"
                  name="username"
                  label="Username"
                  value={editForm.username}
                  onChange={handleEditChange}
                  placeholder="Enter username"
                  error={editFieldErrors.username}
                  required
                />

                {!isEditingSuperAdmin ? (
                  <PasswordFormField
                    id="edit-user-password"
                    name="password"
                    label="New password"
                    value={editForm.password}
                    onChange={handleEditChange}
                    placeholder="Leave blank to keep current password"
                    error={editFieldErrors.password}
                    required={false}
                    autoComplete="new-password"
                  />
                ) : null}

                {isEditingSuperAdmin ? (
                  <div className="form-group">
                    <label htmlFor="edit-user-role-readonly">Role</label>
                    <input
                      id="edit-user-role-readonly"
                      className="super-admin-readonly-input"
                      type="text"
                      value={formatRoleLabel(editForm.role)}
                      readOnly
                      disabled
                    />
                  </div>
                ) : (
                  <FormSelect
                    id="edit-user-role"
                    name="role"
                    label="Role"
                    value={editForm.role}
                    onChange={handleEditChange}
                    options={ROLE_OPTIONS}
                    placeholder="Select role"
                    error={editFieldErrors.role}
                    required
                  />
                )}

                {editRequiresTannery && !isEditingSuperAdmin ? (
                  <FormSelect
                    id="edit-user-tannery_id"
                    name="tannery_id"
                    label="Tannery"
                    value={editForm.tannery_id}
                    onChange={handleEditChange}
                    options={tanneryOptions}
                    placeholder={isLoadingTanneries ? "Loading tanneries..." : "Select tannery"}
                    error={editFieldErrors.tannery_id}
                    required
                    disabled={isLoadingTanneries}
                  />
                ) : null}
              </div>
              <div className="modal-footer">
                <PrimaryButton type="button" onClick={closeEditModal} disabled={isSavingEdit}>
                  Cancel
                </PrimaryButton>
                <PrimaryButton type="submit" disabled={isSavingEdit}>
                  {isSavingEdit ? "Saving..." : "Save changes"}
                </PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default SuperAdminUsersPage;
